import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CornerDownRight, Search } from 'lucide-react';
import { WorldSection } from './WorldPageHeader';
import { itemName, itemPath, mapName } from '../../utils/worldFormat';
import { monsterDrops, routeKindLabel } from '../../utils/worldQueries';
import { monsterItemDrops, qualityLabel, routeDisabled, routeQuality } from '../../utils/itemSources';
import { targetWithheld } from '../../utils/invasionValidation';
import { bagContents, monsterBags } from '../../utils/bagSources';
import { DataStatus } from './DataStatus';
import { normalize } from '../../utils/text';
import { ItemThumb } from './ItemSprite';
import type { WorldData } from '../../types/world';
const PAGE = 30;

/** Búsqueda inversa: qué ítems puede soltar este monstruo y en qué mapas (route_targets.csv). */
export function MonsterItemDrops({
  w,
  monsterId
}: {
  w: WorldData;
  monsterId: number;
}) {
  const drops = useMemo(() => monsterItemDrops(w, monsterId), [w, monsterId]);
  const legacy = useMemo(() => {
    const known = new Set(drops.map(d => d.itemKey));
    const d = monsterDrops(w, monsterId);
    return d.eventItems.filter(e => e.itemId && !known.has(e.itemId));
  }, [w, monsterId, drops]);
  const [q, setQ] = useState('');
  const [showIndirect, setShowIndirect] = useState(true);
  const [limit, setLimit] = useState(PAGE);
  const status = useMemo(() => dropStatus(w, monsterId), [w, monsterId]);
  const active = drops.filter(d => !d.disabled);
  const disabledCount = drops.length - active.length;
  const nq = normalize(q);
  const filtered = active.filter(d => (showIndirect || !d.indirect) && (!nq || normalize(d.name).includes(nq)));
  const indirectCount = active.filter(d => d.indirect).length;
  return <WorldSection id="drops" title="Ítems que puede soltar" count={active.length} data-id="element-1769" data-mp="31xz7rpidw4ho">
      {active.length === 0 && legacy.length === 0 ? <EmptyDropsMessage status={status} data-id="element-1770" data-mp="1qgzkj8aw8fui" /> : <>
          <p className="mb-4 text-sm text-gray-400" data-id="element-1771" data-mp="3t3enzm28747u">
            Combinaciones posibles según la configuración del servidor. No son drops garantizados.
            {disabledCount > 0 ? ` ${disabledCount} rutas desactivadas no se muestran.` : ''}
          </p>
          {status.heldRows + status.heldBagResults > 0 && <p className="mb-4 flex flex-wrap items-center gap-2 text-sm text-gray-300" data-id="element-1772" data-mp="3ef0dcoykibgq">
              <DataStatus kind="conflict" data-id="element-1773" data-mp="39f5mumipfnw7" /> Además, hay recompensas documentadas cuya publicación está en revisión y no se muestran.
            </p>}
          {active.length > 8 && <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-1774" data-mp="30eh6hl5u9bg5">
              <label className="relative flex-1" data-id="element-1775" data-mp="30gk3wq9bm6uw">
                <span className="sr-only" data-id="element-1776" data-mp="2vgnycmkz67d8">Filtrar ítems</span>
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" aria-hidden="true" data-id="element-1777" data-mp="yn4350jirbm7" />
                <input value={q} onChange={e => {
            setQ(e.target.value);
            setLimit(PAGE);
          }} placeholder="Filtrar por nombre de ítem…" className="h-11 w-full rounded-full border border-white/15 bg-[#050b14] pl-10 pr-4 text-sm text-white placeholder:text-gray-500 focus:border-[#D4AF37]/60 focus:outline-none" data-id="element-1778" data-mp="tpko3yezyut7" />
              </label>
              {indirectCount > 0 && <label className="flex h-11 items-center gap-2 text-sm text-gray-300" data-id="element-1779" data-mp="iar3szk0g8e4">
                  <input type="checkbox" checked={showIndirect} onChange={e => setShowIndirect(e.target.checked)} className="h-4 w-4 accent-[#D4AF37]" data-id="element-1780" data-mp="n8j7equ0piy5" />
                  Incluir obtención indirecta ({indirectCount})
                </label>}
            </div>}
          <ul className="grid gap-2 md:grid-cols-2" data-id="element-1781" data-mp="23l7r32i8e8de">
            {filtered.slice(0, limit).map(d => {
          const routes = d.routes.filter(r => !r.disabled);
          const maps = Array.from(new Set(routes.flatMap(r => r.mapIds)));
          const kinds = Array.from(new Set(routes.map(r => r.indirect || r.route.viaItemIds.length ? 'Al abrir una caja o bolsa' : r.eventIds.some(e => e.startsWith('invasion:')) ? 'Recompensa de invasión' : r.eventIds.length ? 'Premio de evento' : r.route.kind === 'quest' ? 'Material de misión · individual, no en party' : r.route.kind === 'natural' || r.route.kind === 'bag' || r.route.kind === 'rule' ? 'Drop del monstruo' : routeKindLabel[r.route.kind] ?? r.route.kind).map((label, i) => routeQuality(routes[i].route, w) === 'normal' ? label : `${label} (${qualityLabel[routeQuality(routes[i].route, w)]})`)));
          const via = Array.from(new Set(routes.filter(r => r.indirect).flatMap(r => r.route.viaItemIds))).map(id => itemName(w, id));
          return <li key={d.itemKey} className="flex gap-3 rounded-2xl border border-white/10 bg-[#0A1628] p-3.5" data-id="element-1782" data-mp="3dhjqgjnxeb1b">
                  <ItemThumb id={d.itemKey} name={d.name} data-id="element-1783" data-mp="3ifbl3lfygb97" />
                  <div className="min-w-0" data-id="element-1784" data-mp="33r7910beqxze">
                  <Link to={`${itemPath(d.itemKey)}#donde-conseguirlo`} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4 hover:text-[#D4AF37]" data-id="element-1785" data-mp="33q5m9yl14n1u">
                    {d.name}
                  </Link>
                  <p className="mt-1 text-xs text-gray-400" data-id="element-1786" data-mp="2zq56a9ia83so">
                    {kinds.join(' · ')}
                    {maps.length ? ` · ${maps.map(m => mapName(w, m)).join(', ')}` : ''}
                  </p>
                  {d.indirect && <p className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-100" data-id="element-1787" data-mp="2prpe037iqohl">
                      <CornerDownRight className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-1788" data-mp="26pf98jyltg0n" />
                      {via.length ? `Indirecto: entrega ${via.join(' o ')}, que puede contenerlo.` : 'Indirecto: no lo entrega directamente.'}
                    </p>}
                  </div>
                </li>;
        })}
            {legacy.map(e => <li key={e.id} className="rounded-2xl border border-white/10 bg-[#0A1628] p-3.5" data-id="element-1789" data-mp="2pm8069kau4rt">
                <Link to={itemPath(e.itemId ?? '')} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4 hover:text-[#D4AF37]" data-id="element-1790" data-mp="2ko6h3omvi5gc">
                  {itemName(w, e.itemId)}
                </Link>
                {e.condition && <p className="mt-1 text-xs text-gray-400" data-id="element-1791" data-mp="149dmib2cfgrb">{e.condition}</p>}
              </li>)}
          </ul>
          {filtered.length === 0 && <p className="text-sm text-gray-400" data-id="element-1792" data-mp="z9iw08mhct6s">Ningún ítem coincide con el filtro.</p>}
          {filtered.length > limit && <button type="button" onClick={() => setLimit(l => l + PAGE)} className="mt-3 h-11 rounded-full border border-white/15 px-5 text-sm font-semibold text-gray-200 hover:border-[#D4AF37]/50 hover:text-white" data-id="element-1793" data-mp="3avkr4ggqp8zz">
              Ver más ({filtered.length - limit} restantes)
            </button>}
        </>}
    </WorldSection>;
}
type DropStatus = {
  /** Las rutas de obtención no llegaron a cargarse. */
  notLoaded: boolean;
  /** Filas de objetivos de este monstruo en la exportación original. */
  rawRows: number;
  /** Filas retenidas por revisión (publicación pendiente). */
  heldRows: number;
  /** Filas cuya ruta está desactivada en la configuración. */
  disabledRows: number;
  /** Resultados de sus recompensas internas (bolsas): visibles y retenidos. */
  visibleBagResults: number;
  heldBagResults: number;
};

/** Distingue: sin datos, datos sin cargar, rutas desactivadas y recompensas retenidas. */
function dropStatus(w: WorldData, monsterId: number): DropStatus {
  const rows = w.idx.routeTargetsByMonster.get(monsterId) ?? [];
  let heldRows = 0;
  let disabledRows = 0;
  rows.forEach(t => {
    const r = w.idx.routeById.get(t.routeId);
    if (!r) return;
    if (targetWithheld(w, r, t)) heldRows++;else if (routeDisabled(w, r)) disabledRows++;
  });
  const bagResults = monsterBags(w, monsterId).flatMap(b => bagContents(w, b.id));
  const heldBagResults = bagResults.filter(c => c.withheld).length;
  return {
    notLoaded: w.routes.length === 0 || w.routeTargets.length === 0,
    rawRows: rows.length,
    heldRows,
    disabledRows,
    visibleBagResults: bagResults.length - heldBagResults,
    heldBagResults
  };
}
function EmptyDropsMessage({
  status
}: {
  status: DropStatus;
}) {
  const held = status.heldRows + status.heldBagResults;
  if (held > 0) {
    return <div className="space-y-2" data-id="element-1794" data-mp="1hf6wylhvy4kq">
        <p className="flex flex-wrap items-center gap-2 text-gray-200" data-id="element-1795" data-mp="1hdvk7w6gpmhh">
          <DataStatus kind="conflict" data-id="element-1796" data-mp="1oxwoey0qrevr" /> Hay recompensas documentadas cuya publicación está en revisión.
        </p>
        <p className="text-sm text-gray-400" data-id="element-1797" data-mp="1txsqustsiy97">
          Los datos existen en la configuración, pero no se muestran hasta que el staff confirme que siguen vigentes.
          {status.visibleBagResults > 0 ? ' Las recompensas ya aprobadas aparecen más abajo.' : ''}
        </p>
      </div>;
  }
  if (status.notLoaded) return <p className="text-gray-400" data-id="element-1798" data-mp="27ph3qybv5qr8">Las rutas de obtención no se cargaron. Recarga la página para ver los ítems de este monstruo.</p>;
  if (status.disabledRows > 0) return <p className="text-gray-400" data-id="element-1799" data-mp="1yx5gflc1qjfh">Sus rutas de obtención están desactivadas en la configuración del servidor, por eso no se muestran ítems.</p>;
  if (status.visibleBagResults > 0) return <p className="text-gray-400" data-id="element-1800" data-mp="2qntf8qq8zcpz">No tiene drops por rutas de obtención. Sus recompensas al derrotarlo aparecen más abajo.</p>;
  return <p className="text-gray-400" data-id="element-1801" data-mp="2verzg1ecwuyi">No hay ítems documentados para este monstruo.</p>;
}